import { type RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react"
import styled from "styled-components"

import { ReactComponent as CloseIcon } from "assets/svg/icon-close.svg"
import { ReactComponent as SearchIcon } from "assets/svg/search.svg"

import { useAnchoredFloatingPanelPosition } from "hooks/useAnchoredFloatingPanelPosition"
import { MonitoringGraphGuide } from "modals/MonitoringGraphGuide"
import { guideTitle } from "modals/MonitoringGraphGuide/GuideChart"
import guideStyles from "modals/MonitoringGraphGuide/index.module.scss"

import { HELP_PANEL_WIDTH } from "./constants"

type VideoGuide = {
  duration: string
  subtitle: string
  title: string
  embedUrl: string
  thumbnailUrl: string
}

type FaqItem = {
  answerLines: string[]
  question: string
}

type FaqSection = {
  items: FaqItem[]
  title: string
}

const videoGuides: VideoGuide[] = [
  {
    title: "How to factory reset your device",
    duration: "0:43",
    subtitle: "Step-by-Step 43 sec",
    embedUrl: "https://www.youtube.com/embed/17-T-Bh5i3g?rel=0&autoplay=1",
    thumbnailUrl: "https://img.youtube.com/vi/17-T-Bh5i3g/hqdefault.jpg",
  },
  {
    title: "Connect & disconnect from a network",
    duration: "0:37",
    subtitle: "Network setup 37 sec",
    embedUrl: "https://www.youtube.com/embed/jD75m8_Bp_k?rel=0&autoplay=1",
    thumbnailUrl: "https://img.youtube.com/vi/jD75m8_Bp_k/hqdefault.jpg",
  },
  {
    title: "How to forget a saved network",
    duration: "0:13",
    subtitle: "Network setup 13 sec",
    embedUrl: "https://www.youtube.com/embed/cLC3t0oG5iM?rel=0&autoplay=1",
    thumbnailUrl: "https://img.youtube.com/vi/cLC3t0oG5iM/hqdefault.jpg",
  },
]

const faqSections: FaqSection[] = [
  {
    title: "Hardware & Installation",
    items: [
      {
        question: "Are there any issues with push-style wiring terminals using 18 AWG wire?",
        answerLines: ["No reported issues, but we’re upgrading terminals in upcoming versions to improve reliability."],
      },
      {
        question: "Can the thermostat support 2-transformer systems?",
        answerLines: [
          "Current backplates only have one R terminal. A new backplate with RC/RH terminals is designed and will enter production soon.",
        ],
      },
      {
        question: "Can I connect an outdoor temperature sensor to the Nuve thermostat?",
        answerLines: [
          "For outdoor temperature and weather forecast Nuve is using 3rd party sources based on Zip (Postal) code provided during installation.",
        ],
      },
      {
        question: "Are we compatible with ductless systems",
        answerLines: [
          'We are currently not compatible with Ductless or "Mini Split" system but are looking at integrating to remote based MS systems later.',
        ],
      },
    ],
  },
  {
    title: "Heating & Cooling Behavior",
    items: [
      {
        question: "Why does my system overshoot the set temperature by 1°F?",
        answerLines: [
          "This is by design. The system stops heating when indoor temp is 1°F above the setpoint, and stops cooling when it’s 1°F below.",
          "Starting with firmware version 1.6.1, users will be able to customize the Turn Off Delta by setting it to either 0°F or 1°F.",
        ],
      },
      {
        question: "Can I customize how and when auxiliary heat activates on Heat Pump system types?",
        answerLines: [
          "Nuve allows threshold customization (e.g. compressor lockout, temp difference) to better manage Aux heat behavior.",
        ],
      },
      {
        question: "How does Dual Fuel mode work (Manual vs Auto)?",
        answerLines: [
          "Manual: Aux heat only runs when selected manually.",
          "Auto: Aux heat activates when outdoor temp drops below the configured lockout threshold and also when during selected via threshold time heat pump can not keep up to reach the set temperature.",
        ],
      },
      {
        question: "How does Emergency Heat mode work?",
        answerLines: [
          "When enabled, the thermostat disables the heat pump and runs only the auxiliary heat.",
          "Emergency heat mode can be manually activated on the System Modes menu on the Thersmostat",
        ],
      },
    ],
  },
  {
    title: "System Logic & Controls",
    items: [
      {
        question: "How does staging work with a heat pump system?",
        answerLines: [
          "By default",
          "Stage 1: temp diff ≥ 1°F",
          "Stage 2: temp diff ≥ 2°F",
          "Stage 3 (Aux): temp diff ≥ 3°F",
          "Aux may also activate if the setpoint isn't reached in 30 min (based on staging logic).",
          "Starting with firmware version 1.6.1, users will have staging control option to customize the temperature difference to engage stage 2 and stage 3",
        ],
      },
      {
        question: "Can I lock out heat strips from engaging too early?",
        answerLines: ["Yes Nuve has that option inside Heat Pump / Dual Fuel configs."],
      },
    ],
  },
  {
    title: "Mobile App & Scheduling",
    items: [
      {
        question: "When will scheduling be available in the mobile app?",
        answerLines: ["Scheduling is available on the Mobile app"],
      },
    ],
  },
  {
    title: "Performance Testing",
    items: [
      {
        question: "Can technicians run a Performance Test from the thermostat?",
        answerLines: [
          "Yes, but only if scheduled beforehand. You can also initiate instant tests from the Monitoring tab.",
        ],
      },
      {
        question: "Can I schedule performance tests for a future time?",
        answerLines: ["Yes."],
      },
      {
        question: "Is there a limit on how often I can run a Performance Test?",
        answerLines: ["No current limits, though future versions may add usage controls."],
      },
    ],
  },
  {
    title: "Alerts & Monitoring",
    items: [
      {
        question: "What alerts are available?",
        answerLines: [
          "On the Contractor’s web dashboard the following ones",
          "",
          "System Efficiency",
          "",
          "Sensor Malfunction related (Temperature, Humidity, Ambient, Air Quality, Fan)",
          "",
          "There are also other alert types shown for Customers on the Device and Mobile app",
        ],
      },
      {
        question: "Who receives alerts?",
        answerLines: [
          "Alerts go to the thermostat, mobile app, contractor dashboard, and contractor’s specified or general email.",
        ],
      },
      {
        question: "Can I customize alert types or recipients?",
        answerLines: ["The feature will be provided in Q3 2026."],
      },
    ],
  },
  {
    title: "Indoor Air Quality",
    items: [
      {
        question: "What does the Air Quality tab measure?",
        answerLines: ["It uses a TVOC sensor to estimate CO₂ and general air quality."],
      },
      {
        question: "What are the air quality levels?",
        answerLines: ["Good: 0–1", "Fair: 1–3", "Poor: >3"],
      },
    ],
  },
  {
    title: "ServiceTitan Integration",
    items: [
      {
        question: "How often does Nuve sync with ServiceTitan?",
        answerLines: [
          "It pulls customer/job data once during installation using the Job#, but soon we will add once a day data auto-pulling / syncing possibility too.",
        ],
      },
    ],
  },
  {
    title: "Orders & Grouping",
    items: [
      {
        question: "Can I group customers or filter messages?",
        answerLines: [
          "Yes, filtering by customer type and opt-in status is supported. Grouping by tech or job is on our roadmap.",
        ],
      },
      {
        question: "Can we add a PO field to track orders?",
        answerLines: ["Yes we are working on adding tracking option directly into Web dashboard too."],
      },
    ],
  },
]

const ModalContainer = styled.div`
  position: fixed;
  width: ${HELP_PANEL_WIDTH}px;
  max-height: calc(100vh - 80px);
  background: var(--surface-1);
  border-radius: 8px;
  box-shadow:
    0px 24px 60px -16px rgba(15, 23, 42, 0.22),
    0px 8px 24px -12px rgba(71, 85, 105, 0.18);
  display: flex;
  flex-direction: column;
  z-index: 4100;
  overflow: hidden;

  @media only screen and (max-width: 500px) {
    right: 10px;
    width: min(${HELP_PANEL_WIDTH}px, calc(100vw - 20px));
  }
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-bottom: 1px solid var(--border-default);
`

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
`

const HeaderContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const Title = styled.h3`
  flex: 1;
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  letter-spacing: -0.27px;
  color: var(--text-primary);
  margin: 0;
`

const Subtitle = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: 13px;
  line-height: 18px;
`

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`

const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: var(--surface-3);
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`

const SearchField = styled.label`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border: 1px solid var(--border-default);
  border-radius: 12px;
  background: var(--surface-2);

  svg {
    flex-shrink: 0;
  }
`

const SearchInput = styled.input`
  width: 100%;
  border: 0;
  background: transparent;
  outline: none;
  color: var(--text-primary);
  font-size: 14px;

  &::placeholder {
    color: var(--text-disabled);
  }
`

const SectionLabel = styled.div`
  color: var(--text-disabled);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 16px;
`

const VideoList = styled.div`
  display: grid;
  gap: 12px;
  margin-bottom: 18px;
`

const VideoCard = styled.article`
  overflow: hidden;
  border: 1px solid var(--border-default);
  border-radius: 12px;
  background: var(--surface-1);
`

const VideoThumbnailButton = styled.button`
  display: block;
  width: 100%;
  position: relative;
  padding: 0;
  border: 0;
  cursor: pointer;
  color: inherit;
  background: transparent;

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 3px rgb(43 178 200 / 22%);
  }
`

const VideoThumbnailImage = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  background: var(--status-info-subtle);
`

const PlayOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, rgb(15 23 42 / 4%) 0%, rgb(15 23 42 / 16%) 100%);
`

const PlayButton = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 999px;
  background: rgb(15 23 42 / 70%);
  border: 1px solid rgb(255 255 255 / 18%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 24px rgb(15 23 42 / 24%);

  &::before {
    content: "";
    margin-left: 3px;
    width: 0;
    height: 0;
    border-top: 8px solid transparent;
    border-bottom: 8px solid transparent;
    border-left: 12px solid #ffffff;
  }
`

const DurationBadge = styled.div`
  position: absolute;
  right: 8px;
  bottom: 8px;
  padding: 2px 6px;
  border-radius: 4px;
  background: rgb(15 23 42 / 72%);
  color: #ffffff;
  font-size: 10px;
  line-height: 1.2;
`

const VideoFrame = styled.iframe`
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  border: 0;
`

const VideoContent = styled.div`
  padding: 10px 12px 12px;
`

const VideoTitle = styled.h4`
  color: var(--text-primary);
  font-size: 13px;
  font-weight: 500;
  line-height: 1.35;
  margin: 0;
`

const VideoMeta = styled.p`
  margin: 2px 0 0;
  color: var(--text-disabled);
  font-size: 11px;
  line-height: 1.35;
`

const FaqList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const FaqItemWrap = styled.details`
  border-radius: 12px;
  border: 1px solid var(--border-default);
  background: var(--surface-1);
  overflow: hidden;
`

const FaqSummary = styled.summary`
  position: relative;
  list-style: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 48px 16px 16px;
  color: var(--text-primary);
  font-size: 14px;
  font-weight: 500;

  &::-webkit-details-marker {
    display: none;
  }
`

const FaqChevron = styled.span`
  position: absolute;
  top: 50%;
  right: 16px;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: var(--text-muted);
  line-height: 1;

  &::before {
    content: "";
    width: 7px;
    height: 7px;
    border-right: 1.75px solid currentColor;
    border-top: 1.75px solid currentColor;
    transform: rotate(-45deg);
  }

  ${FaqItemWrap}[open] &::before {
    transform: rotate(135deg);
  }
`

const FaqAnswer = styled.div`
  padding: 0 16px 14px;
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.55;
  white-space: pre-line;
`

const NoticeSection = styled.section`
  margin-top: 16px;
  padding: 14px 16px;
  border: 1px solid #d7e2f0;
  border-radius: 12px;
  background: #f8fbff;
`

const NoticeText = styled.p`
  margin: 0;
  color: var(--text-muted);
  font-size: 13px;
  line-height: 1.55;
  text-align: left;
  white-space: normal;
  overflow-wrap: anywhere;
`

const SearchIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

interface Props {
  onCancel: () => void
  triggerRef: RefObject<HTMLDivElement | null>
  isSidesheet?: boolean
}

export const HelpFaqModal = ({ onCancel, triggerRef, isSidesheet }: Props) => {
  const [isGuideOpen, setIsGuideOpen] = useState(false)
  const [guidePanelHeight, setGuidePanelHeight] = useState<number>()
  const modalRef = useRef<HTMLDivElement>(null)
  const [query, setQuery] = useState("")
  const [activeVideoTitle, setActiveVideoTitle] = useState<string | null>(null)
  const [activeFaqQuestion, setActiveFaqQuestion] = useState<string | null>(null)
  const normalizedQuery = query.trim().toLowerCase()
  const isTestEnvironment = import.meta.env.MODE === "test"
  const modalPosition = useAnchoredFloatingPanelPosition({
    anchorRef: triggerRef,
    panelSize: { width: HELP_PANEL_WIDTH, height: 720 },
    gap: 8,
    viewportPadding: 10,
  })

  const filteredFaqSections = useMemo(() => {
    if (!normalizedQuery) return faqSections

    return faqSections
      .map(section => {
        const items = section.items.filter(item =>
          `${section.title} ${item.question} ${item.answerLines.join(" ")}`.toLowerCase().includes(normalizedQuery),
        )

        return items.length > 0 ? { ...section, items } : null
      })
      .filter((section): section is FaqSection => section !== null)
  }, [normalizedQuery])

  const filteredVideoGuides = useMemo(() => {
    if (!normalizedQuery) return videoGuides

    return videoGuides.filter(item => `${item.title} ${item.subtitle}`.toLowerCase().includes(normalizedQuery))
  }, [normalizedQuery])

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (isGuideOpen) return
      if (modalRef.current && !modalRef.current.contains(event.target as HTMLElement)) {
        onCancel()
      }
    },
    [onCancel, isGuideOpen],
  )

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (isGuideOpen) return
      if (event.key === "Escape") {
        if (isSidesheet) {
          event.stopImmediatePropagation()
          event.preventDefault()
          if (event.type === "keydown") return
        }
        onCancel()
      }
    },
    [onCancel, isGuideOpen, isSidesheet],
  )

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside)
    if (isSidesheet) {
      window.addEventListener("keydown", handleEscape, true)
      window.addEventListener("keyup", handleEscape, true)
    } else document.addEventListener("keydown", handleEscape)

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleEscape)
      window.removeEventListener("keydown", handleEscape, true)
      window.removeEventListener("keyup", handleEscape, true)
    }
  }, [handleClickOutside, handleEscape, isSidesheet])

  useLayoutEffect(() => {
    modalRef.current?.focus()
  }, [])

  return (
    <>
      <ModalContainer
        ref={modalRef}
        tabIndex={-1}
        style={
          isSidesheet
            ? {
                right: 0,
                top: 0,
                width: `min(${HELP_PANEL_WIDTH}px, 100vw)`,
                height: "100dvh",
                maxHeight: "100dvh",
                borderRadius: 0,
                zIndex: 6002,
              }
            : { ...modalPosition, height: isGuideOpen ? guidePanelHeight : undefined }
        }
      >
        {isGuideOpen && <MonitoringGraphGuide embedded onClose={onCancel} onBack={() => setIsGuideOpen(false)} />}
        <Header style={isGuideOpen ? { display: "none" } : undefined}>
          <HeaderRow>
            <HeaderContent>
              <Title>Help & FAQ</Title>
              <Subtitle>Video guides and common questions</Subtitle>
            </HeaderContent>
            <IconButton aria-label="Close" onClick={onCancel}>
              <CloseIcon />
            </IconButton>
          </HeaderRow>

          <SearchField>
            <SearchIconWrap>
              <SearchIcon />
            </SearchIconWrap>
            <SearchInput
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Search questions..."
              aria-label="Search questions"
            />
          </SearchField>
        </Header>

        <Body style={isGuideOpen ? { display: "none" } : undefined}>
          {`${guideTitle} For contractors temperature schedule runtime heating cooling`
            .toLowerCase()
            .includes(normalizedQuery) && (
            <section>
              <SectionLabel>GUIDES</SectionLabel>
              <button
                type="button"
                className={guideStyles.guideCard}
                onClick={() => {
                  setGuidePanelHeight(modalRef.current?.getBoundingClientRect().height)
                  setIsGuideOpen(true)
                }}
                aria-label={guideTitle}
              >
                <svg width="60" height="48" viewBox="0 0 60 48" aria-hidden="true">
                  <rect x="1" y="1" width="58" height="46" rx="8" fill="#f8faff" stroke="#d7e2f0" />
                  <path d="M8 18H52" stroke="#22c55e" strokeWidth="2" strokeDasharray="5 3" />
                  <path d="M8 32H20L33 18H52" stroke="#2463eb" strokeWidth="2" fill="none" />
                  <path d="M8 38H52" stroke="#ef4444" strokeWidth="2" strokeDasharray="2 2" />
                </svg>
                <span>
                  {guideTitle}
                  <small>For contractors · 4 min read</small>
                </span>
                <b>New</b>
              </button>
            </section>
          )}
          <section>
            <SectionLabel>VIDEO GUIDES</SectionLabel>
            <VideoList>
              {filteredVideoGuides.map(video => (
                <VideoCard key={video.title}>
                  {activeVideoTitle === video.title ? (
                    <VideoFrame
                      src={isTestEnvironment ? "about:blank" : video.embedUrl}
                      title={video.title}
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen"
                      allowFullScreen
                    />
                  ) : (
                    <VideoThumbnailButton
                      type="button"
                      aria-label={video.title}
                      onClick={() => setActiveVideoTitle(video.title)}
                    >
                      <VideoThumbnailImage
                        src={isTestEnvironment ? "about:blank" : video.thumbnailUrl}
                        alt=""
                        loading="lazy"
                      />
                      <PlayOverlay>
                        <PlayButton />
                      </PlayOverlay>
                      <DurationBadge>{video.duration}</DurationBadge>
                    </VideoThumbnailButton>
                  )}
                  <VideoContent>
                    <VideoTitle>{video.title}</VideoTitle>
                    <VideoMeta>{video.subtitle}</VideoMeta>
                  </VideoContent>
                </VideoCard>
              ))}
            </VideoList>
          </section>

          <section>
            <SectionLabel>FAQ</SectionLabel>
            <FaqList>
              {filteredFaqSections.map(section => (
                <div key={section.title}>
                  <SectionLabel>{section.title}</SectionLabel>
                  <FaqList>
                    {section.items.map(item => (
                      <FaqItemWrap key={item.question} open={activeFaqQuestion === item.question}>
                        <FaqSummary
                          onClick={event => {
                            event.preventDefault()
                            setActiveFaqQuestion(prev => (prev === item.question ? null : item.question))
                          }}
                        >
                          <span>{item.question}</span>
                          <FaqChevron />
                        </FaqSummary>
                        <FaqAnswer>{item.answerLines.join("\n")}</FaqAnswer>
                      </FaqItemWrap>
                    ))}
                  </FaqList>
                </div>
              ))}
            </FaqList>
          </section>
          <NoticeSection>
            <NoticeText>
              Please contact your Customer Success Rep. in case you can&apos;t find what you&apos;re looking for?
            </NoticeText>
          </NoticeSection>
        </Body>
      </ModalContainer>
    </>
  )
}
